import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Reusable Button Component
 * 
 * @param {Object} props
 * @param {string} props.variant - Button style variant: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' (default: 'primary')
 * @param {string} props.size - Button size: 'sm' | 'md' | 'lg' (default: 'md')
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.loading - Loading state (shows spinner)
 * @param {boolean} props.fullWidth - Makes button full width
 * @param {React.ReactNode} props.icon - Icon element to display before text
 * @param {React.ReactNode} props.iconRight - Icon element to display after text
 * @param {string} props.className - Additional custom classes
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} props.onClick - Click handler
 * @param {string} props.type - Button type (default: 'button')
 */
export const Button = React.forwardRef(({
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon: Icon,
  iconRight: IconRight,
  className = '',
  children,
  onClick,
  type = 'button',
  ...rest
}, ref) => {
  // Base styles
  const baseStyles = 'tw:inline-flex tw:items-center tw:justify-center tw:font-normal tw:rounded-lg tw:transition-all tw:duration-200 focus:tw:outline-none focus:tw:ring-2 focus:tw:ring-offset-2 tw:border disabled:tw:cursor-not-allowed';

  // Variant styles with proper disabled states
  const variantStyles = {
    primary: 'tw:bg-primary tw:text-white tw:border-primary hover:tw:!bg-blue-600 focus:tw:ring-primary disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
    secondary: 'tw:bg-[#4B5563] tw:text-white tw:border-[#4B5563] hover:tw:!bg-[#374151] focus:tw:ring-gray-500 disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
    outline: 'tw:bg-transparent tw:text-gray-700 tw:border-gray-300 hover:tw:!bg-gray-50 focus:tw:ring-gray-400 disabled:tw:bg-gray-50 disabled:tw:border-gray-200 disabled:tw:text-gray-400',
    ghost: '!tw:bg-transparent tw:text-gray-700 !tw:border-transparent hover:!tw:bg-gray-100 focus:tw:ring-gray-400 disabled:!tw:bg-transparent disabled:tw:text-gray-400',
    danger: 'tw:bg-red-600 tw:text-white tw:border-red-600 hover:tw:!bg-red-700 focus:tw:ring-red-500 disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
    'outline-danger': 'tw:bg-transparent tw:text-red-600 tw:border-red-600 hover:tw:!bg-red-50 focus:tw:ring-red-500 disabled:tw:bg-gray-50 disabled:tw:border-gray-200 disabled:tw:text-gray-400',
    success: 'tw:bg-green-600 tw:text-white tw:border-green-600 hover:tw:!bg-green-700 focus:tw:ring-green-500 disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
    warning: 'tw:bg-yellow-600 tw:text-white tw:border-yellow-600 hover:tw:!bg-yellow-700 focus:tw:ring-yellow-500 disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
  };

  // Size styles (padding and gap only, text size will be applied inline)
  const sizeStyles = {
    sm: 'tw:px-2.5 tw:py-1.5 tw:gap-1.5',
    md: 'tw:px-3 tw:py-1.5 tw:gap-1.5',
    lg: 'tw:px-4 tw:py-2 tw:gap-2',
  };

  // Text size styles (separate for easier application)
  const textSizeStyles = {
    sm: 'tw:text-xs',      // 12px
    md: 'tw:text-sm',      // 14px
    lg: 'tw:text-base',    // 16px
  };

  // Width styles
  const widthStyles = fullWidth ? 'tw:w-full' : '';

  // Icon size based on button size
  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 16,
  };

  const iconSize = iconSizes[size];

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${textSizeStyles[size]} ${widthStyles} ${className}`}
      {...rest}
    >
      {loading ? (
        <>
          <Loader2 size={iconSize} className="tw:animate-spin" />
          {children && <span className={textSizeStyles[size]}>{children}</span>}
        </>
      ) : (
        <>
          {Icon && <Icon size={iconSize} />}
          {children && <span className={textSizeStyles[size]}>{children}</span>}
          {IconRight && <IconRight size={iconSize} />}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';

/**
 * Icon-only Button Component
 */
export const IconButton = React.forwardRef(({
  variant = 'ghost',
  size = 'md',
  icon: Icon,
  disabled = false,
  loading = false,
  className = '',
  onClick,
  type = 'button',
  ...rest
}, ref) => {
  const baseStyles = 'tw:inline-flex tw:items-center tw:justify-center tw:font-normal tw:rounded-lg tw:transition-all tw:duration-200 focus:tw:outline-none focus:tw:ring-2 focus:tw:ring-offset-2 tw:border disabled:tw:cursor-not-allowed';

  const variantStyles = {
    primary: 'tw:bg-primary tw:text-white tw:border-primary hover:tw:bg-blue-600 focus:tw:ring-primary disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
    secondary: 'tw:bg-gray-600 tw:text-white tw:border-gray-600 hover:tw:bg-gray-700 focus:tw:ring-gray-500 disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
    outline: 'tw:bg-transparent tw:text-gray-700 tw:border-gray-300 hover:tw:bg-gray-50 focus:tw:ring-gray-400 disabled:tw:bg-gray-50 disabled:tw:border-gray-200 disabled:tw:text-gray-400',
    ghost: 'tw:bg-transparent tw:text-gray-700 tw:border-transparent hover:tw:bg-gray-100 focus:tw:ring-gray-400 disabled:tw:bg-transparent disabled:tw:text-gray-400',
    danger: 'tw:bg-red-600 tw:text-white tw:border-red-600 hover:tw:bg-red-700 focus:tw:ring-red-500 disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
    'outline-danger': 'tw:bg-transparent tw:text-red-600 tw:border-red-600 hover:tw:bg-red-50 focus:tw:ring-red-500 disabled:tw:bg-gray-50 disabled:tw:border-gray-200 disabled:tw:text-gray-400',
    success: 'tw:bg-green-600 tw:text-white tw:border-green-600 hover:tw:bg-green-700 focus:tw:ring-green-500 disabled:tw:bg-gray-300 disabled:tw:border-gray-300 disabled:tw:text-gray-500',
  };

  const sizeStyles = {
    sm: 'tw:p-1',
    md: 'tw:p-1.5',
    lg: 'tw:p-2',
  };

  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 18,
  };

  const iconSize = iconSizes[size];

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={`${baseStyles} ${variantStyles[variant]} ${sizeStyles[size]} ${className}`}
      {...rest}
    >
      {loading ? (
        <Loader2 size={iconSize} className="tw:animate-spin" />
      ) : (
        Icon && <Icon size={iconSize} />
      )}
    </button>
  );
});

IconButton.displayName = 'IconButton';

/**
 * Button Group Component
 */
export const ButtonGroup = ({ children, className = '' }) => {
  return (
    <div className={`tw:inline-flex tw:rounded-lg tw:shadow-sm ${className}`} role="group">
      {React.Children.map(children, (child, index) => {
        if (!React.isValidElement(child)) return child;
        
        const isFirst = index === 0;
        const isLast = index === React.Children.count(children) - 1;
        
        let roundedClasses = '';
        if (isFirst && !isLast) {
          roundedClasses = 'tw:rounded-r-none';
        } else if (isLast && !isFirst) {
          roundedClasses = 'tw:rounded-l-none tw:-ml-px';
        } else if (!isFirst && !isLast) {
          roundedClasses = 'tw:rounded-none tw:-ml-px';
        }
        
        return React.cloneElement(child, {
          className: `${child.props.className || ''} ${roundedClasses}`.trim(),
        });
      })}
    </div>
  );
};

export default Button;

